(() => {
  if (customElements.get('exhibition-product')) return;
  class ExhibitionProduct extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.form = this.querySelector('.exhibition-form');
      if (!this.form) return;
      this.messages = this.form.querySelector('[data-exhibition-messages]')?.dataset || {};
      this.storageKey = 'exhibition-contact:' + this.dataset.productId;
      // Retain only submitted fields in this tab for a failed submission / challenge.
      // No localStorage, marketing registration or external request is used.
      this.restoreDraft();
      this.updateInquiryFields();
      this.form.addEventListener('change', event => {
        if (event.target.matches('[data-exhibition-inquiry-type]')) this.updateInquiryFields();
      }, { signal });
      this.form.addEventListener('focusin', event => {
        if (event.target.matches('[data-exhibition-visit-date]')) this.updateVisitMinDate();
      }, { signal });
      this.form.addEventListener('submit', event => {
        if (this.form.querySelector('[data-exhibition-success]')) {
          event.preventDefault();
          return;
        }
        // Recheck the lower date bound if the form remained open across midnight.
        this.updateVisitMinDate();
        if (!this.form.reportValidity()) {
          event.preventDefault();
          return;
        }
        this.saveDraft();
      }, { signal });
      document.addEventListener('variant:change', (event) => {
        if (event.target?.getAttribute('id') === this.dataset.productForm) this.updateVariant(event.detail.variant);
      }, { signal });
      this.addEventListener('click', event => {
        const trigger = event.target.closest('[data-exhibition-inquiry]');
        if (!trigger) return;
        const field = this.form.querySelector('[data-exhibition-inquiry-type]');
        if (field && [...field.options].some(option => option.value === trigger.dataset.exhibitionInquiry)) {
          field.value = trigger.dataset.exhibitionInquiry;
          field.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, { signal });
      const initial = this.querySelector('variant-picker script[data-variant]');
      if (initial) {
        try { this.updateVariant(JSON.parse(initial.textContent)); } catch { /* Keep server values. */ }
      }
      const result = this.form.querySelector('[data-exhibition-success], [data-exhibition-error]');
      this.modal = this.form.closest('x-modal');
      if (result?.hasAttribute('data-exhibition-success')) {
        const heading = document.getElementById(this.modal.getAttribute('aria-labelledby'));
        if (heading) {
          heading.dataset.contactHeading ||= heading.textContent;
          heading.textContent = result.textContent;
          heading.classList.add('sr-only');
        }
      }
      this.modal.addEventListener('dialog:after-hide', () => this.resetSuccessfulInquiry(), { signal });
      this.modal.addEventListener('dialog:after-show', () => {
        if (!this.focusFreshInquiry) return;
        requestAnimationFrame(() => {
          if (signal.aborted || !this.modal.open) return;
          this.form.querySelector('[name="contact[name]"]')?.focus({ preventScroll: true });
          this.focusFreshInquiry = false;
        });
      }, { signal });
      // Shopify contact submissions reload the page. Reopen the result instead
      // of leaving success or validation errors inside a hidden dialog.
      if (result || location.hash === '#' + this.form.getAttribute('id')) {
        customElements.whenDefined('x-modal').then(async () => {
          if (!this.isConnected || signal.aborted) return;
          if (result) this.modal.setAttribute('initial-focus', '[data-exhibition-success], [data-exhibition-error]');
          await this.modal.show(false);
          if (!signal.aborted) result?.focus({ preventScroll: true });
        });
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      // Prestige moves open modals to body. Clean up an open modal when the
      // owning product section is replaced, but allow synchronous reattachment.
      queueMicrotask(() => {
        if (!this.isConnected && this.modal?.parentElement === document.body) this.modal.remove();
      });
    }
    resetSuccessfulInquiry() {
      const result = this.form.querySelector('[data-exhibition-success]');
      if (!result) return;
      const fields = this.form.querySelector('[data-exhibition-contact-fields]');
      if (!fields) return;
      result.remove();
      this.draftFields().forEach(field => {
        if (field instanceof HTMLSelectElement) field.selectedIndex = 0;
        else if (field.type === 'checkbox' || field.type === 'radio') field.checked = false;
        else field.value = '';
      });
      this.focusFreshInquiry = true;
      fields.hidden = false;
      fields.disabled = false;
      const heading = document.getElementById(this.modal.getAttribute('aria-labelledby'));
      if (heading) {
        heading.textContent = heading.dataset.contactHeading || heading.textContent;
        heading.classList.remove('sr-only');
      }
      this.modal.setAttribute('initial-focus', '[name="contact[name]"]');
      this.updateInquiryFields();
      if (this.currentVariant !== undefined) this.updateVariant(this.currentVariant);
    }
    updateInquiryFields() {
      const select = this.form.querySelector('[data-exhibition-inquiry-type]');
      const option = select?.selectedOptions[0];
      if (!option) return;
      const label = this.form.querySelector('[data-exhibition-body-label]');
      const help = this.form.querySelector('[data-exhibition-body-help]');
      if (label && option.dataset.bodyLabel) label.textContent = option.dataset.bodyLabel;
      if (help && option.dataset.bodyHelp) help.textContent = option.dataset.bodyHelp;
      const shipping = this.form.querySelector('[data-exhibition-shipping-details]');
      if (shipping) {
        const active = select.value === '配送・納期';
        shipping.hidden = !active;
        shipping.querySelectorAll('input').forEach(field => { field.disabled = !active; });
      }
      this.updateVisitDetails();
    }
    updateVisitDetails() {
      const details = this.form.querySelector('[data-exhibition-visit-details]');
      if (!details) return;
      const visiting = this.form.querySelector('[data-exhibition-inquiry-type]')?.value === '来店見学';
      details.hidden = !visiting;
      this.updateVisitMinDate();
      // Keep visit-only data out of photo/shipping inquiries, retaining typed dates if reselected.
      details.querySelectorAll('input, select').forEach(field => { field.disabled = !visiting; });
    }
    updateVisitMinDate() {
      const input = this.form.querySelector('[data-exhibition-visit-date]');
      if (!input) return;
      // Visits take place in Japan. Add calendar days using the current Japanese date.
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit'
      }).formatToParts(new Date());
      const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
      const earliest = new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day) + 5));
      input.min = earliest.toISOString().slice(0, 10);
      const hint = this.form.querySelector('[data-exhibition-visit-earliest]');
      if (hint) {
        const date = new Intl.DateTimeFormat(document.documentElement.lang || 'ja', {
          timeZone: 'UTC', year: 'numeric', month: 'long', day: 'numeric'
        }).format(earliest);
        hint.textContent = hint.dataset.message.replace('__DATE__', date);
      }
    }
    updateVariant(variant) {
      this.currentVariant = variant;
      if (this.form.querySelector('[data-exhibition-success]')) return;
      const valid = Boolean(variant?.id);
      const available = valid && variant.available;
      this.form.querySelector('[data-exhibition-variant-input]').value = valid ? variant.title : (this.messages.noVariant || '');
      this.form.querySelector('[data-exhibition-variant-id]').value = valid ? variant.id : '';
      this.form.querySelector('[data-exhibition-variant-label]').textContent = valid ? variant.title : (this.messages.noVariant || '');
      this.form.querySelector('[data-exhibition-availability]').textContent = !valid
        ? (this.messages.unavailable || '')
        : available ? (this.messages.available || '') : (this.messages.sold || '');
      const url = new URL(this.dataset.productUrl, location.origin);
      if (valid) url.searchParams.set('variant', variant.id);
      this.form.querySelector('[data-exhibition-url-input]').value = url.href;
      this.form.querySelector('[data-exhibition-product-url]').href = url.href;
      this.querySelectorAll('[data-exhibition-consult-link]').forEach(link => {
        link.textContent = available ? (this.messages.consultAvailable || '') : (this.messages.consultSold || '');
      });
    }
    draftFields() {
      return Array.from(this.form.elements).filter(field =>
        field.name?.startsWith('contact[') && field.type !== 'hidden' && field.type !== 'submit');
    }
    saveDraft() {
      try {
        const values = Object.fromEntries(this.draftFields().map(field => [field.name, field.value]));
        sessionStorage.setItem(this.storageKey, JSON.stringify({ time: Date.now(), values }));
      } catch { /* Storage can be unavailable in private browsing. */ }
    }
    restoreDraft() {
      try {
        if (this.form.querySelector('[data-exhibition-success]')) {
          sessionStorage.removeItem(this.storageKey);
          return;
        }
        const draft = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        if (!draft) return;
        if (Date.now() - draft.time > 30 * 60 * 1000) {
          sessionStorage.removeItem(this.storageKey);
          return;
        }
        for (const field of this.draftFields()) {
          const value = draft.values?.[field.name];
          if (typeof value !== 'string') continue;
          // Ignore retired inquiry options saved before the form was updated.
          if (field instanceof HTMLSelectElement && ![...field.options].some(option => option.value === value)) continue;
          field.value = value;
        }
      } catch { /* Shopify-rendered form values remain available. */ }
    }
  }
  customElements.define('exhibition-product', ExhibitionProduct);
})();
